import { Button, Modal, Table, message, Input, Select, DatePicker, Switch, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  SortAscendingOutlined,
  EyeOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import React, { useEffect, useState, useContext } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { ZONE } from '../../../constants/common';
import { GameProfileQuestion, GameProfileQuestionSummaryResponse } from '../../../types/gameProfileQuestion';
import {
  getGameProfileQuestionsRepository,
  GameProfileQuestionSet,
  GameProfileQuestionRequestParams,
  createGameProfileQuestionRepository,
  updateGameProfileQuestionRepository,
  deleteGameProfileQuestionRepository,
  updateGameProfileQuestionStatusRepository,
  getGameProfileQuestionSummaryRepository,
  sortGameProfileQuestionsRepository,
} from '../../../repository/mytm/gameProfile/gameProfileQuestionsRepository';
import {
  updateGameProfileQuestionSetStatusRepository,
  deleteGameProfileQuestionSetRepository,
} from '../../../repository/mytm/gameProfile/gameProfileQuestionSetsRepository';
import { logging } from '../../../utils/log';
import { MESSAGE_ERROR } from '../../../constants/message';
import { GAME_PROFILE } from '../../../constants/define/translation';
import QuestionForm from './QuestionForm';
import { GameProfileQuestionSummaryModal } from './GameProfileQuestionSummaryModal';
import { ConfirmAction } from '../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../repository/context/userContext';
import { postLoginRepository } from '../../../repository/mytm/auth/loginRepository';
import { IconCreateBlack } from '../../../assets';
import { QuestionSetModal } from './QuestionSetModal';

const { Option } = Select;

interface TableQuestionsProps {
  onRefresh?: () => void;
  country?: string | null;
}

interface TableRow {
  key: string;
  type: 'set' | 'question';
  id: string;
  name: string;
  answerType?: string;
  createdAt: string;
  startTime?: string | null;
  endTime?: string | null;
  disabled: boolean;
  question?: GameProfileQuestion;
  set?: GameProfileQuestionSet;
}

// Custom arrow icons
const UpArrow: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <svg width="12" height="8" viewBox="0 0 10 6" fill={disabled ? '#d9d9d9' : 'currentColor'}>
    <path d="M5 0L10 6H0L5 0Z" />
  </svg>
);

const DownArrow: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <svg width="12" height="8" viewBox="0 0 10 6" fill={disabled ? '#d9d9d9' : 'currentColor'}>
    <path d="M5 6L0 0H10L5 6Z" />
  </svg>
);

export const TableQuestions: React.FC<TableQuestionsProps> = ({ onRefresh, country }) => {
  const { t } = useTranslation(GAME_PROFILE);
  const userContext = useContext(UserContext);

  const ANSWER_TYPE_OPTIONS = [
    { label: t('tableQuestions.answerTypeLabels.singleOneColumn'), value: 'SINGLE_ONE_COLUMN' },
    { label: t('tableQuestions.answerTypeLabels.singleTwoColumns'), value: 'SINGLE_TWO_COLUMNS' },
    { label: t('tableQuestions.answerTypeLabels.dropdownWithSearch'), value: 'DROPDOWN_WITH_SEARCH' },
    { label: t('tableQuestions.answerTypeLabels.search'), value: 'SEARCH' },
    { label: t('tableQuestions.answerTypeLabels.imageTile'), value: 'IMAGE_TILE' },
  ];
  const [questionSets, setQuestionSets] = useState<GameProfileQuestionSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<GameProfileQuestion | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [summaryData, setSummaryData] = useState<GameProfileQuestionSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [editSetModalVisible, setEditSetModalVisible] = useState(false);
  const [editingQuestionSetId, setEditingQuestionSetId] = useState<string | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [selectedAnswerType, setSelectedAnswerType] = useState<string | undefined>(undefined);
  const [fromDate, setFromDate] = useState<moment.Moment | null>(null);
  const [toDate, setToDate] = useState<moment.Moment | null>(null);
  // Track if filters have been applied (via Apply Filters button)
  const [filtersApplied, setFiltersApplied] = useState(false);

  const loadQuestions = async (params?: GameProfileQuestionRequestParams) => {
    try {
      setLoading(true);
      const requestParams = {
        ...params,
        page: params?.page || currentPage,
        limit: params?.limit || pageSize,
        country: country || 'USA',
      };
      const response = await getGameProfileQuestionsRepository(requestParams);
      setQuestionSets(response.questionSets || []);
      setTotal(response.total || 0);
    } catch (error) {
      logging(error);
      message.error(t('tableQuestions.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    // Trim search text to avoid whitespace-only searches
    const trimmedSearchText = searchText.trim();
    // Update state to show trimmed value in input
    setSearchText(trimmedSearchText);

    const params: GameProfileQuestionRequestParams = {
      search: trimmedSearchText || undefined,
      answerType: selectedAnswerType,
      fromDate: fromDate?.tz(ZONE).startOf('day').toISOString(),
      toDate: toDate?.tz(ZONE).endOf('day').toISOString(),
      page: 1,
      limit: pageSize,
    };
    // Only mark filters as applied if there are actual filter values
    const hasFilterValues = !!(trimmedSearchText || selectedAnswerType || fromDate || toDate);
    setFiltersApplied(hasFilterValues);
    loadQuestions(params);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedAnswerType(undefined);
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
    // Clear applied filters flag - sorting will be enabled again
    setFiltersApplied(false);
    loadQuestions({ page: 1, limit: pageSize });
  };

  const handleToggleDisabled = async (id: string, disabled: boolean, type: 'set' | 'question') => {
    if (type === 'set') {
      Modal.confirm({
        title: t('questionSetModal.popup.title'),
        okText: t('questionSetModal.popup.okBtn'),
        cancelText: t('questionSetModal.popup.cancelBtn'),
        icon: <ExclamationCircleOutlined />,
        content: (
          <ConfirmAction
            textConfirmAction={disabled ? t('questionSetModal.popup.deactivate') : t('questionSetModal.popup.activate')}
            isConfirmPassword
            textConfirmPassword={t('questionSetModal.popup.confirmPassword')}
          />
        ),
        onOk() {
          setLoading(true);
          const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
          const password = (inputPasswordNode as any).value;
          if (!userContext.user?.email || userContext.user?.email === '') {
            setLoading(false);
            message.error(MESSAGE_ERROR.M02);
          } else {
            postLoginRepository({
              email: userContext.user?.email,
              password,
            })
              .then(async () => {
                try {
                  await updateGameProfileQuestionSetStatusRepository(id, disabled);
                  await loadQuestions();
                  message.success(t('questionSetModal.messages.statusUpdated'));
                } catch (error: any) {
                  logging(error);
                  message.error(error?.errorMessage || t('questionSetModal.messages.statusUpdateFailed'), 8);
                } finally {
                  setLoading(false);
                }
              })
              .catch((e: any) => {
                setLoading(false);
                message.error(e?.errorMessage);
              });
          }
        },
      });
      return;
    }

    // Handle question status update
    try {
      setLoading(true);
      await updateGameProfileQuestionStatusRepository(id, disabled);
      loadQuestions();
      message.success(t('tableQuestions.messages.statusUpdated'));
    } catch (error) {
      logging(error);
      message.error(t('tableQuestions.messages.statusUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (question: GameProfileQuestion) => {
    setEditingQuestion(question);
    setIsCreating(false);
    setIsViewOnly(question.isAnswered || false);
    setEditModalVisible(true);
  };

  const handleDelete = (id: string, type: 'set' | 'question', name: string) => {
    Modal.confirm({
      title: t('tableQuestions.modal.confirmDelete'),
      okText: t('tableQuestions.modal.okText'),
      cancelText: t('tableQuestions.modal.cancelText'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={`Are you sure you want to delete this ${type}? "${name}"`}
          isConfirmPassword
          textConfirmPassword={t('tableQuestions.modal.textConfirmPassword')}
        />
      ),
      onOk() {
        setLoading(true);
        const updateFunc = () => {
          if (type === 'question') {
            deleteGameProfileQuestionRepository(id)
              .then(() => {
                message.success(t('tableQuestions.messages.deleteSuccess'));
                loadQuestions();
              })
              .catch(() => message.error(t('tableQuestions.messages.deleteFailed')))
              .finally(() => {
                setLoading(false);
              });
          } else if (type === 'set') {
            deleteGameProfileQuestionSetRepository(id)
              .then(() => {
                message.success(t('tableQuestions.messages.questionSetDeleteSuccess'));
                loadQuestions();
              })
              .catch(() => message.error(t('tableQuestions.messages.questionSetDeleteFailed')))
              .finally(() => {
                setLoading(false);
              });
          }
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setLoading(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleReport = async (id: string) => {
    try {
      setSummaryLoading(true);
      setSummaryModalVisible(true);
      const response = await getGameProfileQuestionSummaryRepository(id);
      setSummaryData(response);
    } catch (error) {
      logging(error);
      message.error(t('tableQuestions.messages.summaryLoadFailed'));
      setSummaryModalVisible(false);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSaveQuestion = async (values: any) => {
    try {
      setLoading(true);
      const payload = { ...values };
      if (editingQuestion?.id) {
        await updateGameProfileQuestionRepository({ ...payload, id: editingQuestion.id });
        message.success(t('tableQuestions.messages.updateSuccess'));
      } else {
        await createGameProfileQuestionRepository(payload);
        message.success(t('tableQuestions.messages.createSuccess'));
      }
      setEditModalVisible(false);
      setEditingQuestion(null);
      setIsCreating(false);
      loadQuestions();
    } catch (error: any) {
      const apiError = error?.errorMessage || error?.message;
      logging(error);
      message.error(apiError || t('tableQuestions.messages.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditModalVisible(false);
    setEditingQuestion(null);
    setIsCreating(false);
    setIsViewOnly(false);
  };

  const handleSummaryModalCancel = () => {
    setSummaryModalVisible(false);
    setSummaryData(null);
  };

  const getModalTitle = () => {
    if (isCreating) return t('tableQuestions.modal.addQuestion');
    if (isViewOnly) return t('tableQuestions.modal.viewQuestion');
    return t('tableQuestions.modal.editQuestion');
  };

  // Transform data for table
  const tableData: TableRow[] = questionSets.flatMap(set => {
    const setRow: TableRow = {
      key: `set-${set.gameProfileGroupSetId}`,
      type: 'set',
      id: set.gameProfileGroupSetId,
      name: set.gameProfileGroupSetName,
      createdAt: set.createdAt,
      startTime: set.startTime,
      endTime: set.endTime,
      disabled: set.disabled,
      set,
    };

    const questionRows: TableRow[] = set.questions.map((question, index) => ({
      key: `question-${question.id}`,
      type: 'question',
      id: question.id,
      name: question.name,
      answerType: question.answerType,
      createdAt: question.createdAt,
      disabled: question.disabled,
      question,
    }));

    return [setRow, ...questionRows];
  });

  // Handle moving question up within its set
  const handleMoveUp = (record: TableRow, currentIndex: number) => {
    if (record.type !== 'question' || !record.question) return;

    const setId = record.question.gameProfileGroupSetId;

    // Find all questions in this set
    const questionsInSet = tableData
      .filter(row => row.type === 'question' && row.question?.gameProfileGroupSetId === setId)
      .map(row => row.question!);

    const questionIndex = questionsInSet.findIndex(q => q.id === record.id);

    // Can't move up if it's the first question
    if (questionIndex === 0) return;

    // Swap with previous question
    const reorderedQuestions = [...questionsInSet];
    [reorderedQuestions[questionIndex - 1], reorderedQuestions[questionIndex]] = [
      reorderedQuestions[questionIndex],
      reorderedQuestions[questionIndex - 1],
    ];

    // Create payload and call API
    const payload = {
      questions: reorderedQuestions.map((question, index) => ({
        id: question.id,
        sortOrder: index + 1,
      })),
    };

    setLoading(true);
    sortGameProfileQuestionsRepository(payload)
      .then(() => {
        loadQuestions();
      })
      .catch(error => {
        logging(error);
        const apiError = error?.errorMessage || error?.message;
        message.error(apiError || t('tableQuestions.messages.moveQuestionFailed'));
        setLoading(false);
      });
  };

  // Handle moving question down within its set
  const handleMoveDown = (record: TableRow, currentIndex: number) => {
    if (record.type !== 'question' || !record.question) return;

    const setId = record.question.gameProfileGroupSetId;

    // Find all questions in this set
    const questionsInSet = tableData
      .filter(row => row.type === 'question' && row.question?.gameProfileGroupSetId === setId)
      .map(row => row.question!);

    const questionIndex = questionsInSet.findIndex(q => q.id === record.id);

    // Can't move down if it's the last question
    if (questionIndex === questionsInSet.length - 1) return;

    // Swap with next question
    const reorderedQuestions = [...questionsInSet];
    [reorderedQuestions[questionIndex], reorderedQuestions[questionIndex + 1]] = [
      reorderedQuestions[questionIndex + 1],
      reorderedQuestions[questionIndex],
    ];

    // Create payload and call API
    const payload = {
      questions: reorderedQuestions.map((question, index) => ({
        id: question.id,
        sortOrder: index + 1,
      })),
    };

    setLoading(true);
    sortGameProfileQuestionsRepository(payload)
      .then(() => {
        loadQuestions();
      })
      .catch(error => {
        logging(error);
        const apiError = error?.errorMessage || error?.message;
        message.error(apiError || t('tableQuestions.messages.moveQuestionFailed'));
        setLoading(false);
      });
  };

  // Check if filters have been applied (via Apply Filters button)
  // Sorting is only enabled after Clear Filters is clicked to refresh the list
  const hasActiveFilters = filtersApplied;

  const columns: ColumnsType<TableRow> = [
    {
      title: t('tableQuestions.columns.number'),
      dataIndex: 'index',
      key: 'index',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: TableRow, index: number) => {
        if (record.type === 'set') {
          return null;
        }
        // Calculate question number within the set and position info
        const setIndex = tableData.findIndex(r => r.key === record.key);
        const previousSetIndex = tableData.slice(0, setIndex).findLastIndex((r: any) => r.type === 'set');
        const questionIndex = setIndex - previousSetIndex;

        // Find all questions in this set to determine first/last
        const setId = record.question?.gameProfileGroupSetId;
        const questionsInSet = tableData.filter(
          row => row.type === 'question' && row.question?.gameProfileGroupSetId === setId,
        );
        const positionInSet = questionsInSet.findIndex(q => q.key === record.key);
        const isFirst = positionInSet === 0;
        const isLast = positionInSet === questionsInSet.length - 1;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
            <span style={{ fontWeight: 500 }}>{questionIndex}</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', lineHeight: 0 }}>
              <Button
                type="text"
                size="small"
                disabled={isFirst || hasActiveFilters}
                onClick={e => {
                  e.stopPropagation();
                  handleMoveUp(record, index);
                }}
                icon={<UpArrow disabled={isFirst || hasActiveFilters} />}
                title={hasActiveFilters ? 'Clear filters to enable sorting' : ''}
                style={{
                  padding: 0,
                  margin: 0,
                  width: 22,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 'auto',
                  lineHeight: 0,
                }}
              />
              <Button
                type="text"
                size="small"
                disabled={isLast || hasActiveFilters}
                onClick={e => {
                  e.stopPropagation();
                  handleMoveDown(record, index);
                }}
                icon={<DownArrow disabled={isLast || hasActiveFilters} />}
                title={hasActiveFilters ? 'Clear filters to enable sorting' : ''}
                style={{
                  padding: 0,
                  margin: 0,
                  width: 22,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 'auto',
                  lineHeight: 0,
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: t('tableQuestions.columns.question'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: TableRow) => {
        if (record.type === 'set') {
          return <SetTitle>{text}</SetTitle>;
        }
        return <QuestionText>{text}</QuestionText>;
      },
    },
    {
      title: t('tableQuestions.columns.answerType'),
      dataIndex: 'answerType',
      key: 'answerType',
      width: 250,
      align: 'center' as const,
      render: (answerType: string, record: TableRow) => {
        if (record.type === 'set' || !answerType) return null;
        const option = ANSWER_TYPE_OPTIONS.find(opt => opt.value === answerType);
        return <AnswerTypeBadge>{option?.label || answerType}</AnswerTypeBadge>;
      },
    },
    {
      title: t('tableQuestions.columns.createDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      align: 'center' as const,
      render: (date: string, record: TableRow) => {
        return <DateText>{moment(date).tz(ZONE).format('MM/DD/YYYY')}</DateText>;
      },
    },
    {
      title: t('tableQuestions.columns.scheduleDate'),
      dataIndex: 'scheduleDate',
      key: 'scheduleDate',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: TableRow) => {
        if (record.type === 'question') return null;
        if (!record.startTime && !record.endTime) return null;
        const start = record.startTime ? moment(record.startTime).tz(ZONE).format('MM/DD/YYYY') : '—';
        const end = record.endTime ? moment(record.endTime).tz(ZONE).format('MM/DD/YYYY') : '—';
        return (
          <DateText>
            {start} - {end}
          </DateText>
        );
      },
    },
    {
      title: t('tableQuestions.columns.active'),
      dataIndex: 'disabled',
      key: 'disabled',
      width: 100,
      align: 'center' as const,
      render: (disabled: boolean, record: TableRow) => {
        // Check if this question belongs to a disabled set
        const isQuestionInDisabledSet =
          record.type === 'question' &&
          record.question &&
          questionSets.find(set => set.gameProfileGroupSetId === record.question?.gameProfileGroupSetId)?.disabled;

        if (isQuestionInDisabledSet) {
          return (
            <Tooltip title={t('tableQuestions.tooltips.questionSetDisabled')}>
              <Switch checked={!disabled} disabled loading={loading} />
            </Tooltip>
          );
        }

        return (
          <Switch
            checked={!disabled}
            onChange={checked => handleToggleDisabled(record.id, !checked, record.type)}
            loading={loading}
          />
        );
      },
    },
    {
      title: t('tableQuestions.columns.action'),
      key: 'action',
      width: 150,
      align: 'center' as const,
      render: (_: any, record: TableRow) => {
        if (record.type === 'set') {
          const isActive = !record.disabled;
          return (
            <ActionButtons>
              <Tooltip
                title={
                  record.disabled ? t('questionSetModal.tooltips.edit') : t('questionSetModal.tooltips.editDisabled')
                }
              >
                <span style={{ display: 'inline-block' }}>
                  <ActionButton
                    onClick={() => !isActive && handleEditSet(record.id)}
                    disabled={isActive}
                    style={{
                      cursor: isActive ? 'not-allowed' : 'pointer',
                      opacity: isActive ? 0.5 : 1,
                      pointerEvents: isActive ? 'none' : 'auto',
                    }}
                  >
                    <EditOutlined />
                  </ActionButton>
                </span>
              </Tooltip>
              <Tooltip title={isActive ? t('tableQuestions.tooltips.deactivateBeforeDelete') : ''}>
                <span style={{ display: 'inline-block' }}>
                  <ActionButton
                    danger
                    disabled={isActive}
                    onClick={() => !isActive && handleDelete(record.id, 'set', record.name)}
                    style={{
                      cursor: isActive ? 'not-allowed' : 'pointer',
                      opacity: isActive ? 0.5 : 1,
                      pointerEvents: isActive ? 'none' : 'auto',
                    }}
                  >
                    <DeleteOutlined />
                  </ActionButton>
                </span>
              </Tooltip>
            </ActionButtons>
          );
        }
        if (record.question) {
          return (
            <ActionButtons>
              <ActionButton onClick={() => handleEdit(record.question!)}>
                {record?.question?.isAnswered ? <EyeOutlined /> : <EditOutlined />}
              </ActionButton>
              <ActionButton onClick={() => handleReport(record.id)}>
                <BarChartOutlined />
              </ActionButton>
              <ActionButton danger onClick={() => handleDelete(record.id, 'question', record.name)}>
                <DeleteOutlined />
              </ActionButton>
            </ActionButtons>
          );
        }
        return null;
      },
    },
  ];

  const handleCreate = () => {
    setEditingQuestion(null);
    setIsCreating(true);
    setEditModalVisible(true);
  };

  const handleEditSet = (id: string) => {
    setEditingQuestionSetId(id);
    setEditSetModalVisible(true);
  };

  const handleSetModalSuccess = () => {
    loadQuestions();
    setEditSetModalVisible(false);
    setEditingQuestionSetId(null);
  };

  const handleSetModalCancel = () => {
    setEditSetModalVisible(false);
    setEditingQuestionSetId(null);
  };

  return (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle>Questions</StyledTitle>
        <StyledButtonGroup>
          <Button
            type="text"
            icon={<SortAscendingOutlined />}
            style={{
              color: hasActiveFilters ? '#8c8c8c' : '#28a745',
              fontWeight: 500,
            }}
          >
            {hasActiveFilters ? 'Sorting Disabled' : 'Sorting Enabled'}
          </Button>
          <Button
            type="primary"
            icon={<IconCreateBlack />}
            style={{
              backgroundColor: 'white',
              borderColor: '#000',
              color: '#000',
              borderWidth: '1px',
              borderRadius: '6px',
              fontWeight: 500,
              boxShadow: 'none',
              height: 40,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.borderColor = '#000';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#000';
            }}
            onClick={handleCreate}
          >
            {t('tableQuestions.buttons.addQuestion')}
          </Button>
        </StyledButtonGroup>
      </StyledHeader>
      <FilterSection>
        <FilterRow>
          <FilterItem style={{ flex: 2 }}>
            <FilterLabel>{t('tableQuestions.filterLabels.search')}</FilterLabel>
            <Input
              placeholder={t('tableQuestions.placeholders.searchByQuestionOrSet')}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ borderRadius: '6px', height: '36px' }}
              allowClear
            />
          </FilterItem>
          <FilterItem style={{ flex: 1.5 }}>
            <FilterLabel>{t('tableQuestions.filterLabels.answerType')}</FilterLabel>
            <Select
              placeholder={t('tableQuestions.placeholders.selectAnswerType')}
              value={selectedAnswerType}
              onChange={setSelectedAnswerType}
              allowClear
              style={{ width: '100%', borderRadius: '6px', height: '36px' }}
            >
              {ANSWER_TYPE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </FilterItem>
          <FilterItem>
            <FilterLabel>{t('tableQuestions.filterLabels.createdFrom')}</FilterLabel>
            <DatePicker
              format="MM/DD/YYYY"
              placeholder="00/00/0000"
              value={fromDate}
              onChange={setFromDate}
              disabledDate={current => {
                if (!toDate) return false;
                return current && current.valueOf() > toDate.startOf('day').valueOf();
              }}
              style={{ width: '100%', borderRadius: '6px', height: '36px' }}
            />
          </FilterItem>
          <FilterItem>
            <FilterLabel>{t('tableQuestions.filterLabels.to')}</FilterLabel>
            <DatePicker
              format="MM/DD/YYYY"
              placeholder="00/00/0000"
              value={toDate}
              onChange={setToDate}
              disabledDate={current => {
                if (!fromDate) return false;
                return current && current.valueOf() < fromDate.startOf('day').valueOf();
              }}
              style={{ width: '100%', borderRadius: '6px', height: '36px' }}
            />
          </FilterItem>
          <FilterItem style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: '8px' }}>
            <ApplyButton type="default" icon={<FilterOutlined />} onClick={handleSearch}>
              {t('tableQuestions.buttons.applyFilters')}
            </ApplyButton>
            <ClearButton icon={<DeleteOutlined />} onClick={handleClearFilters}>
              {t('tableQuestions.buttons.clearFilters')}
            </ClearButton>
          </FilterItem>
        </FilterRow>
      </FilterSection>

      <StyledTable
        columns={columns as any}
        dataSource={tableData}
        loading={loading}
        locale={{
          emptyText: (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <div style={{ marginBottom: '16px', color: '#8c8c8c', fontSize: '14px' }}>
                {hasActiveFilters ? t('tableQuestions.emptyText') : t('tableQuestions.noQuestionsAdded')}
              </div>
              {hasActiveFilters ? null : (
                <Button
                  type="primary"
                  icon={<IconCreateBlack />}
                  style={{
                    backgroundColor: 'white',
                    borderColor: '#000',
                    color: '#000',
                    borderWidth: '1px',
                    borderRadius: '6px',
                    fontWeight: 500,
                    boxShadow: 'none',
                    height: 40,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#000';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#000';
                  }}
                  onClick={handleCreate}
                >
                  {t('tableQuestions.buttons.addQuestion')}
                </Button>
              )}
            </div>
          ),
        }}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['15', '30', '50', '100'],
          onChange: (page, size) => {
            setCurrentPage(page);
            const newSize = size || pageSize;
            if (newSize !== pageSize) {
              setPageSize(newSize);
              setCurrentPage(1);
            }
            loadQuestions({
              search: searchText || undefined,
              answerType: selectedAnswerType,
              fromDate: fromDate?.tz(ZONE).startOf('day').toISOString(),
              toDate: toDate?.tz(ZONE).endOf('day').toISOString(),
              page: newSize !== pageSize ? 1 : page,
              limit: newSize,
            });
          },
        }}
        summary={() => {
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={7} index={0}>
                  Total: {total}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
        rowClassName={((record: TableRow) => (record.type === 'set' ? 'set-row' : 'question-row')) as any}
      />

      <Modal
        title={getModalTitle()}
        visible={editModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        destroyOnClose
        zIndex={3}
      >
        <QuestionForm
          questionId={editingQuestion?.id}
          onSave={handleSaveQuestion}
          loading={loading}
          onCancel={handleCancel}
          readOnly={isViewOnly}
          country={country}
        />
      </Modal>

      <GameProfileQuestionSummaryModal
        visible={summaryModalVisible}
        onCancel={handleSummaryModalCancel}
        summaryData={summaryData}
        loading={summaryLoading}
      />

      <QuestionSetModal
        visible={editSetModalVisible}
        onCancel={handleSetModalCancel}
        onSuccess={handleSetModalSuccess}
        questionSetId={editingQuestionSetId}
        country={country}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  background-color: #f5f5f5;
  min-height: 100vh;
  padding: 24px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const StyledTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #262626;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FilterSection = styled.div`
  padding: 10px 0;
  margin-bottom: 16px;
  border-radius: 8px;

  .ant-select-selector {
    border-radius: 6px !important;
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-arrow {
    color: #000 !important;
  }

  .ant-picker {
    border-radius: 6px;

    .ant-picker-suffix {
      background-color: #cbcbcb;
      color: #000 !important;
      padding: 8px 8px;
      margin-right: -11px;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      height: 34px;
      display: flex;
      align-items: center;
    }

    .ant-picker-clear {
      color: #000 !important;
      border-radius: 50%;
    }
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 700;
  color: #000;
`;

const ApplyButton = styled(Button)`
  height: 36px;
  border-radius: 6px;
  border-color: #000;
  font-size: 12px;
  font-weight: 700;
  padding: 0 8px;

  .anticon {
    font-size: 16px;
    vertical-align: middle;
  }
`;

const ClearButton = styled(Button)`
  height: 36px;
  border-radius: 6px;
  background-color: transparent;
  border: none;
  color: #000;
  box-shadow: none;
  padding: 0 0;
  font-size: 12px;
  font-weight: 700;

  &:hover {
    background-color: #f5f5f5 !important;
    border: none !important;
    color: #000 !important;
  }

  &:focus {
    background-color: transparent;
    border: none;
    color: #000;
  }
`;

const StyledTable = styled(Table)`
  background: white;
  border-radius: 8px;

  .ant-table-thead > tr > th {
    background-color: #fafafa;
    color: #262626;
    font-weight: 600;
    font-size: 13px;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .set-row {
    background-color: #e5e5e5;
    font-weight: 600;

    &:hover {
      background-color: #e5e5e5 !important;

      > td {
        background-color: #e5e5e5 !important;
      }
    }
  }

  .question-row {
    background-color: white;

    &:hover {
      background-color: #fafafa !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
  }
`;

const SetTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #262626;
  position: relative;
  left: -116px; /* Move left to cover number column (100px width + 16px padding) */
  width: calc(100% + 116px); /* Extend width to cover number column */
  padding-left: 16px;
  text-transform: uppercase;
`;

const QuestionText = styled.div`
  font-size: 14px;
  color: #262626;
`;

const AnswerTypeBadge = styled.div`
  background-color: #bee8ff;
  color: #026298;
  padding: 8px 13px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  display: inline-block;
  border: 1px solid #0693e4;
  min-width: 190px;
`;

const DateText = styled.span`
  font-size: 13px;
  color: #595959;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const ActionButton = styled(Button)`
  padding: 4px 8px;
  height: auto;
  border: none;
  background: #f5f5f5;

  &:hover {
    background-color: #f0f0f0;
  }

  ${props =>
    props.danger &&
    `
    color: #ff4d4f;
    
    &:hover {
      background-color: #fff1f0;
    }
  `}
`;
